import Layout from "../components/Layout";
import { Scale } from "lucide-react";
import { motion } from "motion/react";

interface Section {
  id: string;
  heading: string;
  content: React.ReactNode;
}

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

function TermsList({ items }: { items: string[] }) {
  return (
    <ul className="mt-3 space-y-2">
      {items.map((item) => (
        <li
          key={item}
          className="flex items-start gap-2.5 text-muted-foreground font-body text-base leading-relaxed"
        >
          <span className="mt-1.5 w-2 h-2 rounded-full bg-gold flex-shrink-0" />
          {item}
        </li>
      ))}
    </ul>
  );
}

function TermsParagraph({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-muted-foreground font-body text-base leading-relaxed mt-3">
      {children}
    </p>
  );
}

const sections: Section[] = [
  {
    id: "use-of-website",
    heading: "Use of Website",
    content: (
      <>
        <TermsParagraph>
          The Superhero Jiu Jitsu Academy website is provided for informational
          and communication purposes only. You may use this website to learn
          about our programs, submit inquiries, and communicate with our team.
        </TermsParagraph>
        <TermsParagraph>
          You agree not to use this website for any unlawful purpose or in any
          way that could damage, disable, or interfere with the operation of the
          site or related services.
        </TermsParagraph>
      </>
    ),
  },
  {
    id: "information-accuracy",
    heading: "Information Accuracy",
    content: (
      <TermsParagraph>
        We make reasonable efforts to ensure that information on this website is
        accurate and current. However, Superhero Jiu Jitsu Academy does not
        guarantee that all content is complete, accurate, or up to date. Program
        details, schedules, pricing, and availability may change without notice.
      </TermsParagraph>
    ),
  },
  {
    id: "no-medical-advice",
    heading: "No Medical or Fitness Advice",
    content: (
      <>
        <TermsParagraph>
          Martial arts and physical training involve inherent risks. Information
          provided on this website is not intended as medical, fitness, or
          professional advice.
        </TermsParagraph>
        <TermsParagraph>
          Before participating in any physical activity, you should consult with
          a qualified medical professional. Participation in training programs
          is voluntary and at your own risk.
        </TermsParagraph>
      </>
    ),
  },
  {
    id: "user-submissions",
    heading: "User Submissions",
    content: (
      <>
        <TermsParagraph>
          When you submit information through our website or systems powered by
          Red Belt Gym, you agree that:
        </TermsParagraph>
        <TermsList
          items={[
            "The information provided is accurate and truthful",
            "You have the right to submit the information",
            "You consent to being contacted by Superhero Jiu Jitsu Academy regarding your inquiry",
          ]}
        />
        <TermsParagraph>
          Submission of information does not guarantee enrollment, membership,
          or availability.
        </TermsParagraph>
      </>
    ),
  },
  {
    id: "third-party-services",
    heading: "Third-Party Services",
    content: (
      <>
        <TermsParagraph>
          Our website and online forms may be supported by third-party service
          providers, including Red Belt Software. These providers assist in
          operating our systems and are required to handle information in
          accordance with applicable privacy and security standards.
        </TermsParagraph>
        <TermsParagraph>
          Superhero Jiu Jitsu Academy is not responsible for disruptions or
          issues caused by third-party platforms outside our direct control.
        </TermsParagraph>
      </>
    ),
  },
  {
    id: "intellectual-property",
    heading: "Intellectual Property",
    content: (
      <>
        <TermsParagraph>
          All content on this website, including text, logos, graphics, and
          layout, is the property of Superhero Jiu Jitsu Academy or its
          licensors and is protected by applicable intellectual property laws.
        </TermsParagraph>
        <TermsParagraph>
          You may not copy, reproduce, distribute, or use website content
          without prior written permission, except for personal, non-commercial
          use.
        </TermsParagraph>
      </>
    ),
  },
  {
    id: "limitation-of-liability",
    heading: "Limitation of Liability",
    content: (
      <>
        <TermsParagraph>
          Superhero Jiu Jitsu Academy is not liable for any direct, indirect,
          incidental, or consequential damages arising from your use of this
          website or reliance on its content.
        </TermsParagraph>
        <TermsParagraph>
          Use of this website is at your own risk.
        </TermsParagraph>
      </>
    ),
  },
  {
    id: "indemnification",
    heading: "Indemnification",
    content: (
      <TermsParagraph>
        You agree to indemnify and hold harmless Superhero Jiu Jitsu Academy,
        its owners, staff, and service providers from any claims, damages, or
        expenses arising from your use of this website or violation of these
        Terms of Use.
      </TermsParagraph>
    ),
  },
  {
    id: "governing-law",
    heading: "Governing Law",
    content: (
      <TermsParagraph>
        These Terms of Use are governed by the laws of the State of Texas,
        without regard to conflict of law principles. Any disputes shall be
        resolved in courts located within the State of Texas.
      </TermsParagraph>
    ),
  },
  {
    id: "changes-to-terms",
    heading: "Changes to These Terms",
    content: (
      <TermsParagraph>
        Superhero Jiu Jitsu Academy reserves the right to update or modify these
        Terms of Use at any time. Changes will be posted on this page. Continued
        use of the website constitutes acceptance of the updated terms.
      </TermsParagraph>
    ),
  },
  {
    id: "contact",
    heading: "Contact Information",
    content: (
      <>
        <TermsParagraph>
          If you have questions regarding these Terms of Use, please contact:
        </TermsParagraph>
        <div className="mt-4 bg-muted/50 rounded-xl border border-border p-5 space-y-1.5">
          <p className="font-display font-semibold text-foreground text-base">
            Superhero Jiu Jitsu Academy
          </p>
          <p className="text-muted-foreground font-body text-sm">
            Springfield, OR (Lane County)
          </p>
          <p className="text-muted-foreground font-body text-sm">
            Email:{" "}
            <a
              href="mailto:shjjoffice@gmail.com"
              className="text-foreground font-medium hover:text-gold transition-colors duration-200"
            >
              shjjoffice@gmail.com
            </a>
          </p>
        </div>
      </>
    ),
  },
];

export default function TermsAndConditionsPage() {
  return (
    <Layout>
      <title>Terms and Conditions | Superhero Jiu Jitsu Academy</title>

      {/* Hero banner */}
      <section className="bg-hero py-14 px-4 text-center">
        <motion.div className="container mx-auto max-w-2xl" {...fadeUp(0)}>
          <span className="inline-flex items-center gap-2 text-xs font-display font-semibold tracking-widest uppercase text-gold mb-3">
            <Scale className="w-3.5 h-3.5" />
            Legal
          </span>
          <h1 className="font-display font-black text-3xl sm:text-4xl md:text-5xl text-hero-foreground leading-tight text-balance">
            Terms and Conditions
          </h1>
          <p className="mt-4 text-hero-foreground font-body text-base max-w-lg mx-auto leading-relaxed opacity-90">
            By accessing Superhero Jiu Jitsu Academy's web site, you are
            agreeing to be bound by these web site Terms and Conditions of Use,
            all applicable laws and regulations.
          </p>
        </motion.div>
      </section>

      {/* Gold divider */}
      <div className="h-1 bg-gradient-accent" />

      {/* Intro paragraphs */}
      <section className="bg-background pt-14 px-4">
        <div className="container mx-auto max-w-3xl">
          <motion.div
            {...fadeUp(0)}
            className="bg-card rounded-2xl border border-border p-7 sm:p-8 shadow-card"
          >
            <p className="text-muted-foreground font-body text-base leading-relaxed">
              These Terms of Use govern your access to and use of the Superhero
              Jiu Jitsu Academy website and any related online services,
              including forms and systems powered by Red Belt Gym software. By
              accessing or using this website, you agree to these Terms of Use.
            </p>
            <p className="text-muted-foreground font-body text-base leading-relaxed mt-4">
              If you do not agree with these terms, please do not use this
              website.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Terms sections */}
      <section className="bg-background py-10 px-4">
        <div className="container mx-auto max-w-3xl">
          <div className="space-y-8">
            {sections.map((section, i) => (
              <motion.div
                key={section.id}
                id={section.id}
                {...fadeUp(i * 0.05)}
                className="bg-card rounded-2xl border border-border p-7 sm:p-8 shadow-card"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-1 h-7 rounded-full bg-gradient-accent flex-shrink-0" />
                  <h2 className="font-display font-bold text-xl sm:text-2xl text-foreground">
                    {section.heading}
                  </h2>
                </div>
                <div className="pl-4">{section.content}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom padding */}
      <div className="bg-background pb-14" />
    </Layout>
  );
}
